import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Loader2, Plus, Users, UserCheck, Globe, MessageCircle, ChevronRight, Flame, Trophy, Crown, Dumbbell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

import { PostCard, CreatePostModal } from '../components/social';
import { QuickLinksWheel } from '../components/dashboard/QuickLinksWheel';
import { getImageUrl } from '../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;

export const SocialFeedPage = () => {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [feedType, setFeedType] = useState('all'); // all, following
  const [filterType, setFilterType] = useState('all'); // all, workout, achievement, milestone
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // Followers/Following counts (lists shown on dedicated pages)
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);

  const fetchFollowData = useCallback(async () => {
    try {
      const [followersRes, followingRes] = await Promise.all([
        api().get('/social/followers'),
        api().get('/social/following')
      ]);
      setFollowersCount(followersRes.data.count || 0);
      setFollowingCount(followingRes.data.count || 0);
    } catch (err) {
      // Non-fatal — the header just falls back to 0/0. Log for diagnostics
      // (Sentry-friendly) but don't surface a toast because the feed itself
      // may still be working fine.
      console.error('SocialFeed: failed to fetch follow counts', err);
    }
  }, [api]);

  const fetchPosts = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true);
      setPage(0);
    }
    
    try {
      const skip = reset ? 0 : page * 20;
      const endpoint = feedType === 'following' 
        ? `/social/feed/following?skip=${skip}&limit=20`
        : `/social/feed?skip=${skip}&limit=20&filter_type=${filterType}`;
      
      const res = await api().get(endpoint);
      
      if (reset) {
        setPosts(res.data.posts);
      } else {
        setPosts(prev => [...prev, ...res.data.posts]);
      }
      
      setHasMore(res.data.posts.length === 20);
      setPage(prev => reset ? 1 : prev + 1);
    } catch (err) {
      toast.error('Failed to load posts');
    }
    setLoading(false);
  }, [api, feedType, filterType, page]);

  useEffect(() => {
    fetchPosts(true);
    fetchFollowData();
  }, [feedType, filterType, fetchFollowData]);

  const handlePostCreated = () => {
    fetchPosts(true);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="social-feed-page">

      <main className="container mx-auto px-6 py-8">
        {/* Quick links — same spinnable wheel pattern as the dashboard for
            visual consistency, with the Social-page-specific destinations. */}
        <QuickLinksWheel
          triggerLabel="Connect"
          storageKey="aas:wheel:social:lastIdx"
          links={[
            { to: '/buddies',     Icon: Flame,         label: 'Buddies' },
            { to: '/groups',      Icon: Users,         label: 'Groups' },
            { to: '/discover',    Icon: UserCheck,     label: 'Discover' },
            { to: '/dm',          Icon: MessageCircle, label: 'Messages' },
            { to: '/challenges',  Icon: Trophy,        label: 'Challenges' },
            { to: '/leaderboard', Icon: Crown,         label: 'Leaderboard' },
            { to: '/workouts',    Icon: Dumbbell,      label: 'Workouts' },
          ]}
        />

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Feed */}
          <div className="lg:col-span-3 space-y-6 order-2 lg:order-1">
            {/* Feed Controls */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-blue-500/40 p-4 rounded-2xl electric-glow-blue"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Feed Type Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={feedType === 'all' ? 'default' : 'outline'}
                    onClick={() => setFeedType('all')}
                    className={feedType === 'all' ? 'bg-primary text-black' : ''}
                    data-testid="all-feed-btn"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Everyone
                  </Button>
                  <Button
                    variant={feedType === 'following' ? 'default' : 'outline'}
                    onClick={() => setFeedType('following')}
                    className={feedType === 'following' ? 'bg-primary text-black' : ''}
                    data-testid="following-feed-btn"
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Following
                  </Button>
                </div>

                {/* Filter Type */}
                {feedType === 'all' && (
                  <div className="flex gap-1">
                    {['all', 'workout', 'achievement', 'milestone'].map((type) => (
                      <Button
                        key={type}
                        variant={filterType === type ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setFilterType(type)}
                        className={filterType === type ? 'bg-white/20 text-white' : 'text-muted-foreground'}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Create Post Button */}
                <Button 
                  onClick={() => setShowCreatePost(true)}
                  className="bg-primary text-black"
                  data-testid="create-post-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Post
                </Button>
              </div>
            </motion.div>

            {/* Posts */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-12 w-12 text-primary animate-spin" />
              </div>
            ) : posts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-card border border-border p-12 text-center rounded-2xl"
              >
                <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">
                  {feedType === 'following' ? 'No Posts from People You Follow' : 'No Posts Yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {feedType === 'following' 
                    ? 'Follow other users to see their posts here'
                    : 'Be the first to share your fitness journey!'}
                </p>
                <Button 
                  onClick={() => setShowCreatePost(true)}
                  className="bg-primary text-black"
                >
                  Create Your First Post
                </Button>
              </motion.div>
            ) : (
              <>
                <div className="space-y-6">
                  {posts.map((post, index) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      api={api}
                      currentUserId={user?.id}
                      onPostUpdate={() => fetchPosts(true)}
                      onPostDelete={(postId) => setPosts(posts.filter(p => p.id !== postId))}
                    />
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="text-center py-4">
                    <Button
                      variant="outline"
                      onClick={() => fetchPosts(false)}
                      className="border-primary text-primary"
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar — appears ABOVE the feed on mobile (order-1), to the RIGHT on desktop (lg:order-2) */}
          <div className="space-y-6 order-1 lg:order-2">
            {/* User Stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-card border border-border p-6"
            >
              <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">Your Activity</h3>
              <div className="space-y-3">
                <Link
                  to="/social/followers"
                  className="flex items-center justify-between bg-white/5 hover:bg-white/10 border border-border hover:border-primary/40 rounded-2xl p-4 transition-colors group"
                  data-testid="followers-link"
                >
                  <span className="text-muted-foreground group-hover:text-white flex items-center gap-2 transition-colors">
                    <Users className="h-4 w-4" />
                    Followers
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-white font-heading text-lg">{followersCount}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </span>
                </Link>

                <Link
                  to="/social/following"
                  className="flex items-center justify-between bg-white/5 hover:bg-white/10 border border-border hover:border-primary/40 rounded-2xl p-4 transition-colors group"
                  data-testid="following-link"
                >
                  <span className="text-muted-foreground group-hover:text-white flex items-center gap-2 transition-colors">
                    <UserCheck className="h-4 w-4" />
                    Following
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-white font-heading text-lg">{followingCount}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        api={api}
        onPostCreated={handlePostCreated}
      />
    </div>
  );
};
